import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import cvFile from './assets/pdf/cv.pdf';

const useSound = () => {
  const playHoverSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      gainNode.gain.value = 0.05;
      oscillator.frequency.value = 2000;
      oscillator.type = 'sine';

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.05);
    } catch (error) {
      console.log('Audio playback failed');
    }
  };

  const playClickSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      gainNode.gain.value = 0.1;
      oscillator.frequency.value = 1000;
      oscillator.type = 'sine';

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.log('Audio playback failed');
    }
  };

  return { playHoverSound, playClickSound };
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { playHoverSound, playClickSound } = useSound();

  const toggleMenu = () => {
    playClickSound();
    setIsOpen(!isOpen);
  };

  const handleResumeClick = () => {
    playClickSound();
    window.open(cvFile, '_blank');
  };

  return (
    <Nav>
      <Logo
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        onMouseEnter={playHoverSound}
        to="/"
      >
        Tahia 
      </Logo>

      <RightSection>
        <MenuItems isOpen={isOpen}>
          <MenuItem 
            to="/" 
            onClick={() => {
              playClickSound();
              setIsOpen(false);
            }}
            onMouseEnter={playHoverSound}
          >
            <span>01.</span> Home
          </MenuItem>
          <MenuItem 
            to="/about" 
            onClick={() => {
              playClickSound();
              setIsOpen(false);
            }}
            onMouseEnter={playHoverSound}
          >
            <span>02.</span> About
          </MenuItem>
          <MenuItem 
            to="/projects" 
            onClick={() => {
              playClickSound();
              setIsOpen(false);
            }}
            onMouseEnter={playHoverSound}
          >
            <span>03.</span> Projects
          </MenuItem>
          <MenuItem 
            to="/skills" 
            onClick={() => {
              playClickSound();
              setIsOpen(false);
            }}
            onMouseEnter={playHoverSound}
          >
            <span>04.</span> Skills
          </MenuItem>
          <MenuItem 
            to="/contact" 
            onClick={() => {
              playClickSound();
              setIsOpen(false);
            }}
            onMouseEnter={playHoverSound}
          >
            <span>05.</span> Contact
          </MenuItem>
          <ResumeButton 
            onClick={handleResumeClick}
            onMouseEnter={playHoverSound}
          >
            Resume
          </ResumeButton>
        </MenuItems>

        <HamburgerButton onClick={toggleMenu} onMouseEnter={playHoverSound}>
          <span></span>
          <span></span>
          <span></span>
        </HamburgerButton>
      </RightSection>
    </Nav>
  );
};

const Nav = styled.nav`
  padding: 0.5rem 2rem;  // Reduced height by adjusting padding
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  width: 100%;
  top: 0;
  background: rgba(10, 25, 47, 0.85);
  backdrop-filter: blur(10px);
  z-index: 100;
  background-image: url('/mnt/data/image.png');  // Added background image
  background-size: cover;  // Ensures the image fills the navbar
  background-position: center;  // Centers the image
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;


const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled(motion(Link))`
  font-size: 1.5rem;
  color: #64ffda;
  font-weight: normal;
  text-decoration: none;
  font-family: 'Courier New', monospace;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:before {
    content: '<';
  }

  &:after {
    content: ' />';
  }
`;

const MenuItems = styled.div`
  display: flex;
  align-items: center;
  gap: 2.0rem;

  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 75%;
    background: rgba(10, 25, 47, 0.95);
    padding: 2rem;
    transform: ${({ isOpen }) => isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease-in-out;
    box-shadow: ${({ isOpen }) => isOpen ? '-10px 0px 30px -15px rgba(2,12,27,0.7)' : 'none'};
  }
`;

const MenuItem = styled(motion(Link))`
  color: #ccd6f6;
  text-decoration: none;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Courier New', monospace;
  position: relative;

  span {
    color: #64ffda;
    opacity: 0.7;
  }

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 0;
    background-color: #64ffda;
    transition: width 0.3s ease;
  }

  &:hover:after {
    width: 100%;
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ResumeButton = styled.button`
  padding: 0.75rem 1rem;
  border: 1px solid #64ffda;
  border-radius: 4px;
  background: transparent;
  color: #64ffda;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(100, 255, 218, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    padding: 1rem;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;

  @media (max-width: 768px) {
    display: flex;
  }

  span {
    width: 2rem;
    height: 0.25rem;
    background: #64ffda;
    border-radius: 10px;
    transition: all 0.3s linear;
    position: relative;
    transform-origin: 1px;

    &:first-child {
      transform: ${({ isOpen }) => isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }

    &:nth-child(2) {
      opacity: ${({ isOpen }) => isOpen ? '0' : '1'};
      transform: ${({ isOpen }) => isOpen ? 'translateX(20px)' : 'translateX(0)'};
    }

    &:nth-child(3) {
      transform: ${({ isOpen }) => isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

export default Navbar;